
import { computed, defineComponent, ref, watch, watchEffect } from 'vue'
import { useFormModule } from '@/modules/_shared/forms'
import QForm from 'quasar/src/components/form/QForm.js';
import { useRoute } from 'vue-router'
import EditableSelect from '@/components/_shared/editable-select.vue'
import { useOrganizationModule } from '@/modules/organizations'
import { useMemberModule } from '@/modules/members'

export default defineComponent({
    name: 'members-editable-field-organization',
    props: {
        modelValue: {
            type: String as () => string | null,
            required: true
        }
    },
    components: {
        EditableSelect
    },
    async setup (props, { emit }) {
        const route = useRoute()
        const { getOrganizationsSimplified, organizationsSimplified } = useOrganizationModule()
        const { updateMemberOrganization, deleteMemberFromOrganization } = useMemberModule()
        const { submitForm, createOnSuccess } = useFormModule()

        const form = ref<QForm>()
        const localValue = ref(props.modelValue)
        const failedUpdate = ref(false)
        watchEffect(() => {
            localValue.value = props.modelValue
        })

        watch(localValue, (newOrganizationId, oldOrganizationId) => {
            if (failedUpdate.value) {
                failedUpdate.value = false
                return
            }
            if (newOrganizationId === null && oldOrganizationId !== null) {
                removeMemberFromOrganization(oldOrganizationId)
            } else if (newOrganizationId !== null) {
                changeMemberOrganization(oldOrganizationId, newOrganizationId)
            }
        })

        if (organizationsSimplified.value.length === 0) {
            await getOrganizationsSimplified()
        }
        const mappedTitles = computed(() => organizationsSimplified.value.map(organization => ({
            label: organization.companyName,
            value: organization.id
        })))

        const filteredOptions = ref(mappedTitles.value)
        const filterFn = (val: string, update: (arg: () => void) => void) => {
            update(() => {
                const needle = val.toLowerCase()
                filteredOptions.value = mappedTitles.value.filter(v => v.label.toLowerCase().indexOf(needle) > -1)
            })
        }
        const localOnSuccess = (message: string) => {
            emit('update:modelValue', localValue.value)
            createOnSuccess(message)()
            failedUpdate.value = false
        }
        const localOnApiError = (oldOrganizationId: string | null) => {
            localValue.value = oldOrganizationId
            emit('update:modelValue', localValue.value)
            failedUpdate.value = true
        }

        const removeMemberFromOrganization = (oldOrganizationId: string) => submitForm(
            form.value,
            async () => deleteMemberFromOrganization(route.params.memberId as string, oldOrganizationId),
            () => {
                localOnSuccess('Member removed from organization')
            },
            null,
            () => {
                localOnApiError(oldOrganizationId)
            }
        )

        const changeMemberOrganization = (oldOrganizationId: string | null, newOrganizationId: string) => submitForm(
            form.value,
            async () => updateMemberOrganization(
                route.params.memberId as string,
                oldOrganizationId,
                newOrganizationId
            ),
            () => {
                localOnSuccess("Member's organization updated")
            },
            null,
            () => {
                localOnApiError(oldOrganizationId)
            }
        )

        return {
            filteredOptions,
            localValue,
            form,
            filterFn
        }
    }
})
